/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState, useRef, useLayoutEffect, useMemo } from 'react';

import { MyContext } from '../Store';
import { Sketches } from '../components';
import {
  getSortedColorSketches,
  viewsMap,
  currentDate,
  currentTime,
  getMMValue,
  getMMValueWithInverted,
  getUrlSearchParams,
} from '../utils';

const urlParams = getUrlSearchParams();
const isJaguar = urlParams.get('isJaguar') === 'true';

export const ProductForPdf = () => {
  const [state] = useContext(MyContext);
  const { productData, colorData, productMods, selectedMods, selectedMarking, userData } = state;

  const [isFullSketchRendered, setIsFullSketchRendered] = useState(false);

  const fullSizeSketch = useRef(null);

  const color = productData.available_colors?.find(color => Number(color.id) === Number(productData.id));

  const sortedColorSketches = getSortedColorSketches(colorData.sketches);

  const getModGroupById = id => productMods.find(mod => Number(mod.id) === Number(id));
  const getMarkingById = id => productData.marking.find(marking => Number(marking.id) === Number(id));

  const rotate = selectedMarking?.markingImg?.rotate;
  const isInverted = [90, 270].includes(rotate);

  const markingPositionLeft = useMemo(
    () => getMMValueWithInverted(selectedMarking?.markingImg, isInverted, 'x'),
    [isFullSketchRendered],
  );
  const markingPositionTop = useMemo(
    () => getMMValueWithInverted(selectedMarking?.markingImg, isInverted, 'y'),
    [isFullSketchRendered],
  );
  const markingPositionRight = useMemo(
    () => getMMValueWithInverted(selectedMarking?.markingImg, isInverted, '-x'),
    [isFullSketchRendered],
  );
  const markingPositionBottom = useMemo(
    () => getMMValueWithInverted(selectedMarking?.markingImg, isInverted, '-y'),
    [isFullSketchRendered],
  );
  const markingWidth = useMemo(() => getMMValue(selectedMarking.markingImg?.width, 'x'), [isFullSketchRendered]);
  const markingHeight = useMemo(() => getMMValue(selectedMarking.markingImg?.height, 'y'), [isFullSketchRendered]);

  useLayoutEffect(() => {
    const observer = new ResizeObserver(entries => {
      const el = entries[0].contentRect;
      if (el.width > 0 && el.height > 0) {
        setIsFullSketchRendered(true);
      }
    });
    observer.observe(fullSizeSketch.current);

    return () => fullSizeSketch.current && observer.unobserve(fullSizeSketch.current);
  }, []);

  return (
    <div className="pdf">
      <div className="pdf__page">
        {isJaguar ? (
          <>
            <img src={`${process.env.PUBLIC_URL}/jaguar-logo.png`} alt="Jaguar" />
            <hr />
            <b>Projekt produktu ze strony jaguargift.com</b>
          </>
        ) : (
          <>
            <img src={`${process.env.PUBLIC_URL}/luva-logo.png`} alt="Luva" style={{ maxWidth: '25%' }} />
            <hr />
            <b>Projekt produktu ze strony luva.menu</b>
          </>
        )}
        <p>
          Data/godzina: {currentDate} {currentTime}
        </p>
        <p>Produkt: {`${productData?.name} ${color?.vendo_code}`}</p>
        <p>Kolor: {color?.color?.name}</p>
        {Object.entries(selectedMods).map(modGroup => (
          <p key={`group-${modGroup[0]}`}>
            {getModGroupById(modGroup[0]).name}: {modGroup[1].name}
          </p>
        ))}
        {selectedMarking?.id && (
          <>
            <p>Znakowanie: {getMarkingById(selectedMarking.id)?.name}</p>
            {selectedMarking?.markingImg?.src ? (
              <>
                <p>
                  Wielkość grafiki: {markingWidth} x {markingHeight}
                </p>
                <p>
                  Pozycja grafiki: od lewej: {markingPositionLeft}, od góry: {markingPositionTop}, od prawej:{' '}
                  {markingPositionRight}, od dołu: {markingPositionBottom}
                </p>
                {rotate ? <p>Obrócenie grafiki: {rotate}&#176;</p> : ''}
              </>
            ) : (
              <p style={{ color: 'red' }}>Zamawiający nie przesłał własnego logotypu</p>
            )}
          </>
        )}
        <hr></hr>
        {Object.keys(userData).length > 0 && (
          <div>
            <b>Dane zamawiającego:</b>
            <p>Imię i nazwisko: {`${userData?.firstName} ${userData?.lastName}`}</p>
            <p>
              Adres: {`${userData?.address} ${userData?.addressHouseNumber}`}
              {userData?.addressHouseNumber2 ? `/${userData?.addressHouseNumber2}` : ''}
            </p>
            <p>Miejscowość: {userData?.city}</p>
            {userData?.province && <p>Województwo: {userData?.province}</p>}
            <p>Kod pocztowy: {userData?.postalCode}</p>
            <p>Kod kraju: {userData?.country}</p>
            <p>Nazwa firmy: {userData?.companyName}</p>
            <p>NIP firmy: {userData?.taxNumber}</p>
            <p>Numer telefonu: {userData?.phoneNumber}</p>
            <p>Adres e-mail: {userData?.email}</p>
          </div>
        )}
      </div>
      {sortedColorSketches.map(view => (
        <div className="pdf__page vertical-center" key={view.name} data-view={view.name}>
          <Sketches selectedView={view.name} className="pdf__sketches"></Sketches>
          {view.name === viewsMap.FRONT && (
            <img
              ref={fullSizeSketch}
              id="full-size-sketch"
              src={colorData.sketches?.[view.name]?.image}
              className="sketch"
              alt="main"
            />
          )}
        </div>
      ))}
    </div>
  );
};
